import React, { useState, useEffect } from "react";
import axios from "axios";
import SimpleBackdrop from "./SimpleBackdrop";
import AttachmentViewer from "./AttachmentViewer";
import FullScreenViewer from "./FullScreenViewer";

/**
 * GetMailComponent (server-persistent selections)
 *
 * Notes:
 * - Backend must expose /save_view, /load_view, /delete_view endpoints as in your python_service.py.
 * - When the modal opens we call /load_view to fetch a saved selection (if any).
 * - On every toggle (section / line) we call /save_view to persist the selection server-side.
 */

export default function GetMailComponent(props) {
  const { setMarkerpoint, toDoOnClickOfGetMail, onMailsUpdate } = props;

  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mailSelections, setMailSelections] = useState({}); // Track selections per mail: { mailIndex: { sectionNo: { enabled: bool, lines: {...} } } }
  const [readMoreModal, setReadMoreModal] = useState(null); // { mailIndex, selectedData, subject }

  // modal state
  const [selectedMail, setSelectedMail] = useState(null);
  const [fetchingFull, setFetchingFull] = useState(false);

  // attachment viewer state
  const [viewingAttachments, setViewingAttachments] = useState(null); // mail index
  const [fullScreenAttachment, setFullScreenAttachment] = useState(null);

  // UI state for section selection inside modal
  // shape: { sectionNo: { enabled: bool, lines: {0: true, 1: false, ...} } }
  const [sectionSelection, setSectionSelection] = useState({});

  // keep expanded panel flags for sections
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    // reset selections whenever modal closed
    if (!selectedMail) {
      setSectionSelection({});
      setExpandedSections({});
    }
  }, [selectedMail]);
// format an RFC/ISO/IMAP date string to "15 Nov 2025, 11:43" (fallback to raw if parse fails)
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  // some mail dates include timezone or non-standard formats; try Date parse then fallback
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr; // fallback to raw string if parsing fails

  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Helper: parse speed (kts) from free text (e.g., "17.4 kts", "SOG: 13.5", "13,5 kts")
function parseSpeedFromText(text) {
  if (!text) return null;
  // normalize comma decimals to dot
  const normalized = text.replace(/,(\d+)/g, ".$1");
  // Match common forms: "17.4 kts", "17 kts", "17.4 knots", "SOG: 17.4", "speed:17.4kts"
  const re = /(\d+(?:\.\d+)?)\s*(?:knots?|kts?|kt\b|k\b)/i;
  const m = normalized.match(re);
  if (m) return parseFloat(m[1]);

  const re2 = /(?:SOG|Speed|speed)[:\s]*([0-9]+(?:\.[0-9]+)?)/i;
  const m2 = normalized.match(re2);
  if (m2) return parseFloat(m2[1]);

  // last fallback: any isolated number followed by 'k'
  const re3 = /(\d+(?:\.\d+)?)(?:k\b)/i;
  const m3 = normalized.match(re3);
  if (m3) return parseFloat(m3[1]);

  return null;
}


  // Auto-load 5 ships data on component mount (after login or page refresh)
  useEffect(() => {
    handleGetMail();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  
  // -------------- Server persistence helpers --------------
  const serverSaveSelection = async (mail, selection) => {
    try {
      if (!mail || !selection) return false;
      
      const message_id = mail?.__raw?.Headers?.["Message-ID"] || mail?.__raw?.Headers?.MessageID || null;
      
      // include any known meta values (speed_kts, arrival_status) into payload.meta
      // const baseSelection  = selection.meta || {};
      const selectionPayload =
      selection && typeof selection === "object" ? selection : {};

      const meta = {};
     // always include speed_kts / speed_raw / arrival_status if present on the mail
    if (
      mail.speed_kts !== undefined &&
      mail.speed_kts !== null &&
      !Number.isNaN(Number(mail.speed_kts))
    ) {
      meta.speed_kts = Number(mail.speed_kts);
    }

    if (mail.speed_raw) {
      meta.speed_raw = String(mail.speed_raw);
    }
    if (
      mail.heading_deg !== undefined &&
      mail.heading_deg !== null &&
      !Number.isNaN(Number(mail.heading_deg))
    ) {
      meta.heading_deg = Number(mail.heading_deg);
    }

    if (mail.heading_raw) {
      meta.heading_raw = String(mail.heading_raw);
    }
    if (mail.is_forwarded !== undefined && mail.is_forwarded !== null) {
      meta.is_forwarded = !!mail.is_forwarded;
    }
     if (mail.arrival_status !== undefined && mail.arrival_status !== null) {
      meta.arrival_status = !!mail.arrival_status;
    }
    
      const payload = {
        index: mail?.id ?? null,
        message_id: message_id,
        subject: mail?.__raw?.Subject || mail?.subject,
        date: mail?.__raw?.Date || mail?.received,
        from: mail?.__raw?.From || mail?.from,
        selection: selectionPayload, // may be {}, that’s fine
        meta: { ...meta, saved_at: new Date().toISOString() }
      };
      const resp = await axios.post("https://shipreportstvm.cloud/api/save_view", payload);
      return resp && resp.data && resp.data.ok;
    } catch (e) {
      console.warn("serverSaveSelection failed", e);
      return false;
    }
  };

  const serverLoadSelection = async (mail) => {
    try {
      if (!mail) return null;
      const message_id = mail?.__raw?.Headers?.["Message-ID"] || mail?.__raw?.Headers?.MessageID || null;
      const payload = {
        index: mail?.id ?? null,
        message_id: message_id,
        subject: mail?.__raw?.Subject || mail?.subject,
        date: mail?.__raw?.Date || mail?.received,
        from: mail?.__raw?.From || mail?.from
      };
      const resp = await axios.post("https://shipreportstvm.cloud/api/load_view", payload);
      if (resp && resp.status === 200 && resp.data && resp.data.selection) {
        return resp.data.selection;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const serverDeleteView = async (mail) => {
    try {
      if (!mail) return false;
      const message_id = mail?.__raw?.Headers?.["Message-ID"] || mail?.__raw?.Headers?.MessageID || null;
      const payload = {
        index: mail?.id ?? null,
        message_id: message_id,
        subject: mail?.__raw?.Subject || mail?.subject,
        date: mail?.__raw?.Date || mail?.received,
        from: mail?.__raw?.From || mail?.from
      };
      const resp = await axios.post("https://shipreportstvm.cloud/api/delete_view", payload);
      return resp && resp.data && resp.data.ok;
    } catch (e) {
      return false;
    }
  };

  // Load similar view from same ship based on section titles
  const serverLoadSimilarView = async (mail) => {
    try {
      if (!mail) return null;
      const sections = mail.__raw?.sections || mail.__raw?.sections_filtered || {};
      if (!sections || Object.keys(sections).length === 0) return null;
      
      const from_field = mail?.__raw?.From || mail?.from || mail?.Headers?.From || "";
      const payload = {
        from: from_field,
        sections: sections
      };
      const resp = await axios.post("https://shipreportstvm.cloud/api/load_similar_view", payload);
      if (resp && resp.status === 200 && resp.data && resp.data.selection) {
        return resp.data.selection;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  // -------------- existing mail + UI logic --------------
  // const plotOrRemoveButtonClicked = (pointValue) => {
  //   const copy = [...mails];
  //   if (pointValue.clickedButton === "plot") {
  //     copy[pointValue.itemIndex] = {
  //       ...copy[pointValue.itemIndex],
  //       plotButtonVisible: false,
  //     };
  //     setMails(copy);
  //   } else if (pointValue.clickedButton === "remove") {
  //     copy[pointValue.itemIndex] = {
  //       ...copy[pointValue.itemIndex],
  //       plotButtonVisible: true,
  //     };
  //     setMails(copy);
  //   }
  //   if (setMarkerpoint) setMarkerpoint(pointValue);
  // };

  const handleGetMail = async () => {
    setLoading(true);
    if (toDoOnClickOfGetMail) toDoOnClickOfGetMail();

    try {
      // Fetch from JSON file (updated by background scheduler every 30 min)
      // Use /get_mail_from_json to avoid triggering IMAP fetch on every request
      const response = await axios.get("https://shipreportstvm.cloud/api/get_mail_from_json");
      if (response && response.status === 200 && Array.isArray(response.data)) {
        const result = response.data.map((mailData, idx) => {
          // Handle both structures: with mail and without mail
          // For ships with mail: From, To, Date, Subject, Body, FullBody, Attachments, coordinates
          // For ships without mail: body, attachments, coordinates, status
          const obj = {
            __raw: mailData,
            id: idx,
            from: mailData.From || mailData.from || "",
            to: mailData.To || mailData.to || "",
            received: mailData.Date || mailData.date || "",
            subject: mailData.Subject || mailData.subject || "NA",
            body: mailData.Body || mailData.body || "",
            FullBody: mailData.FullBody || null,
            Headers: mailData.Headers || {
              From: mailData.From || mailData.from || "",
              To: mailData.To || mailData.to || "",
              Subject: mailData.Subject || mailData.subject || "",
              Date: mailData.Date || mailData.date || "",
            },
            // Extract coords from coordinates.coords_extracted or coordinates.coords_extracted
            coords_extracted: (mailData.coordinates && mailData.coordinates.coords_extracted) 
              ? mailData.coordinates.coords_extracted 
              : (mailData.coords_extracted || mailData.coords || null),
            coords: mailData.coordinates || mailData.coords || null,
            ship_name: mailData.ship_name || "Unknown Ship",
            speed_kts: mailData.speed_kts || "",
            is_forwarded: mailData.is_forwarded || "",
            arrival_status: mailData.arrival_status || "",
            // Handle both Attachments (capitalized) and attachments (lowercase)
            // Normalize attachment structure: mimeType -> content_type for compatibility with ShipDataGrid
            attachments: (mailData.Attachments || mailData.attachments || []).map(att => ({
              filename: att.filename || "",
              content_type: att.mimeType || att.content_type || "",
              size: att.size || 0,
              web_path: att.web_path || att.saved_path || "",
              // Keep original fields for backward compatibility
              mimeType: att.mimeType,
              saved_path: att.saved_path,
            })),
            plotButtonVisible: true,
            status: mailData.status || null, // For ships with no mail
          };

          // === NEW: attach speed fields (prefer explicit fields from JSON, fallback to parsing body) ===
obj.speed_kts = (mailData.speed_kts !== undefined && mailData.speed_kts !== null)
  ? Number(mailData.speed_kts)
  : parseSpeedFromText(mailData.FullBody || mailData.Body || mailData.body || "");
obj.speed_raw = mailData.speed_raw || mailData.speedRaw || (obj.speed_kts !== null ? String(obj.speed_kts) + " kts" : null);
// ===== NEW: heading (from backend, fallback to null) =====

obj.heading_deg = (mailData.heading_deg !== undefined && mailData.heading_deg !== null)
  ? Number(mailData.heading_deg)
  : parseSpeedFromText(mailData.FullBody || mailData.Body || mailData.body || "");

obj.heading_raw =
  mailData.heading_raw ||
  mailData.headingRaw ||
  (obj.heading_deg !== null ? `${obj.heading_deg} deg` : null);

// ===== NEW: is_forwarded (normalize string/boolean) =====
          obj.is_forwarded = (mailData.is_forwarded === true) ||
          (typeof mailData.is_forwarded === "string" && mailData.is_forwarded.toLowerCase() === "true") ||
          (mailData.is_forwarded === "1" || mailData.is_forwarded === 1) ? true : false;

 // ===== NEW: arrival status (normalize string/boolean) =====
          obj.arrival_status = (mailData.arrival_status === true) ||
                               (typeof mailData.arrival_status === "string" && mailData.arrival_status.toLowerCase() === "true") ||
                               (mailData.arrival_status === "1" || mailData.arrival_status === 1) ? true : false;

          if (obj.coords_extracted && obj.coords_extracted !== "NA") {
            const parts = (obj.coords_extracted + "").split(",").map(p => p.trim());
            const latVal = parts[0] ? parseFloat(parts[0]) : null;
            const longVal = parts[1] ? parseFloat(parts[1]) : null;
            obj.lat = (!Number.isNaN(latVal) && Number.isFinite(latVal)) ? latVal : null;
            obj.long = (!Number.isNaN(longVal) && Number.isFinite(longVal)) ? longVal : null;

            if (obj.lat !== null && obj.long !== null) {
              obj.plotButtonVisible = false;
            }
          } else {
            obj.lat = null;
            obj.long = null;
          }

          return obj;
        });

        // Load saved selections for all mails BEFORE setting mails (to avoid showing incorrect data)
        const selectionsMap = {};
        await Promise.all(
          result.map(async (mail, index) => {
            try {
              // Try to load saved view for this specific mail first
              let loaded = await serverLoadSelection(mail);
              
              // If no saved view for this mail, try to find similar view from same ship
              if (!loaded || Object.keys(loaded).length === 0) {
                loaded = await serverLoadSimilarView(mail);
              }
              
              if (loaded && typeof loaded === "object") {
                selectionsMap[index] = loaded;
              }
            } catch (err) {
              // Silently fail - selections will be empty if not found
              console.warn(`Failed to load saved selection for mail ${index}:`, err);
            }
          })
        );

        // Set both mails and selections together to avoid showing incorrect data
        setMails(result);
        setMailSelections(selectionsMap);

        if (onMailsUpdate) onMailsUpdate(result);
        Promise.all(
        result.map(async (mail, index) => {
          const existingSel = selectionsMap[index] || {}; // may be {}
          try {
            await serverSaveSelection(mail, existingSel);
          } catch (e) {
            console.warn("Failed to persist meta for mail", index, e);
          }
        })
      );

        if (setMarkerpoint) {
          result.forEach((mail, index) => {
            if (mail.lat !== null && mail.long !== null) {
              setMarkerpoint({
                subject: mail.subject,
                mailContent: mail.body,
                lat: mail.lat,
                long: mail.long,
                ship_name: mail.ship_name,
                attachments: mail.attachments || [],
                received: mail.received || "",
                clickedButton: "plot",
                itemIndex: index,
                speed_kts: mail.speed_kts,
                speed_raw: mail.speed_raw,
                heading_deg: mail.heading_deg,
                heading_raw: mail.heading_raw,
                is_forwarded: mail.is_forwarded,
                arrival_status: mail.arrival_status
              });
            }
          });
        }
      } else {
        // Response is not an array (likely has status field indicating no data)
        // Trigger IMAP fetch as fallback
        throw new Error("No data available in JSON file");
      }
    } catch (err) {
      console.error("Error fetching mail data:", err);
      // If JSON file doesn't exist or error occurred, try to trigger IMAP fetch once
      if (!err.response || err.response.status === 500 || err.response.status === 404 || err.message === "No data available in JSON file") {
        try {
          console.log("JSON file not found or error occurred, triggering initial IMAP fetch...");
          const fallbackResponse = await axios.get("https://shipreportstvm.cloud/api/get_mail");
          if (fallbackResponse && fallbackResponse.status === 200) {
            // Retry with the fetched data
            const result = fallbackResponse.data.map((mailData, idx) => {
              const obj = {
                __raw: mailData,
                id: idx,
                from: mailData.From || mailData.from || "",
                to: mailData.To || mailData.to || "",
                received: mailData.Date || mailData.date || "",
                subject: mailData.Subject || mailData.subject || "NA",
                body: mailData.Body || mailData.body || "",
                FullBody: mailData.FullBody || null,
                Headers: mailData.Headers || {
                  From: mailData.From || mailData.from || "",
                  To: mailData.To || mailData.to || "",
                  Subject: mailData.Subject || mailData.subject || "",
                  Date: mailData.Date || mailData.date || "",
                },
                coords_extracted: (mailData.coordinates && mailData.coordinates.coords_extracted) 
                  ? mailData.coordinates.coords_extracted 
                  : (mailData.coords_extracted || mailData.coords || null),
                coords: mailData.coordinates || mailData.coords || null,
                ship_name: mailData.ship_name || "Unknown Ship",
                attachments: (mailData.Attachments || mailData.attachments || []).map(att => ({
                  filename: att.filename || "",
                  content_type: att.mimeType || att.content_type || "",
                  size: att.size || 0,
                  web_path: att.web_path || att.saved_path || "",
                  mimeType: att.mimeType,
                  saved_path: att.saved_path,
                })),
                plotButtonVisible: true,
                status: mailData.status || null,
              };

              // === NEW: attach speed fields (prefer explicit fields from JSON, fallback to parsing body) ===
obj.speed_kts = (mailData.speed_kts !== undefined && mailData.speed_kts !== null)
  ? Number(mailData.speed_kts)
  : parseSpeedFromText(mailData.FullBody || mailData.Body || mailData.body || "");
obj.speed_raw = mailData.speed_raw || mailData.speedRaw || (obj.speed_kts !== null ? String(obj.speed_kts) + " kts" : null);
// ===== NEW: heading (from backend, fallback to null) =====

obj.heading_deg = (mailData.heading_deg !== undefined && mailData.heading_deg !== null)
  ? Number(mailData.heading_deg)
  : parseSpeedFromText(mailData.FullBody || mailData.Body || mailData.body || "");
obj.heading_raw =
  mailData.heading_raw ||
  mailData.headingRaw ||
  (obj.heading_deg !== null ? `${obj.heading_deg} deg` : null);
// ===== NEW: is_forwarded status (normalize string/boolean) =====
          obj.is_forwarded = (mailData.is_forwarded === true) ||
                               (typeof mailData.is_forwarded === "string" && mailData.is_forwarded.toLowerCase() === "true") ||
                               (mailData.is_forwarded === "1" || mailData.is_forwarded === 1) ? true : false;
 // ===== NEW: arrival status (normalize string/boolean) =====
          obj.arrival_status = (mailData.arrival_status === true) ||
                               (typeof mailData.arrival_status === "string" && mailData.arrival_status.toLowerCase() === "true") ||
                               (mailData.arrival_status === "1" || mailData.arrival_status === 1) ? true : false;

              if (obj.coords_extracted && obj.coords_extracted !== "NA") {
                const parts = (obj.coords_extracted + "").split(",").map(p => p.trim());
                const latVal = parts[0] ? parseFloat(parts[0]) : null;
                const longVal = parts[1] ? parseFloat(parts[1]) : null;
                obj.lat = (!Number.isNaN(latVal) && Number.isFinite(latVal)) ? latVal : null;
                obj.long = (!Number.isNaN(longVal) && Number.isFinite(longVal)) ? longVal : null;

                if (obj.lat !== null && obj.long !== null) {
                  obj.plotButtonVisible = false;
                }
              } else {
                obj.lat = null;
                obj.long = null;
              }

              return obj;
            });

            // Load saved selections for all mails BEFORE setting mails (to avoid showing incorrect data)
            const selectionsMapFallback = {};
            await Promise.all(
              result.map(async (mail, index) => {
                try {
                  // Try to load saved view for this specific mail first
                  let loaded = await serverLoadSelection(mail);
                  
                  // If no saved view for this mail, try to find similar view from same ship
                  if (!loaded || Object.keys(loaded).length === 0) {
                    loaded = await serverLoadSimilarView(mail);
                  }
                  
                  if (loaded && typeof loaded === "object") {
                    selectionsMapFallback[index] = loaded;
                  }
                } catch (err) {
                  // Silently fail - selections will be empty if not found
                  console.warn(`Failed to load saved selection for mail ${index}:`, err);
                }
              })
            );

            // Set both mails and selections together to avoid showing incorrect data
            setMails(result);
            setMailSelections(selectionsMapFallback);
            if (onMailsUpdate) onMailsUpdate(result);
            Promise.all(
  result.map(async (mail, index) => {
    const existingSel = selectionsMapFallback[index] || {};
    try {
      await serverSaveSelection(mail, existingSel);
    } catch (e) {
      console.warn("Failed to persist meta for mail (fallback)", index, e);
    }
  })
);


            if (setMarkerpoint) {
              result.forEach((mail, index) => {
                if (mail.lat !== null && mail.long !== null) {
                  setMarkerpoint({
                    subject: mail.subject,
                    mailContent: mail.body,
                    lat: mail.lat,
                    long: mail.long,
                    ship_name: mail.ship_name,
                    attachments: mail.attachments || [],
                    received: mail.received || "",
                    clickedButton: "plot",
                    itemIndex: index,
                    speed_kts: mail.speed_kts,
                    speed_raw: mail.speed_raw,
                    heading_deg: mail.heading_deg,
                    heading_raw: mail.heading_raw,
                    is_forwarded: mail.is_forwarded,
                    arrival_status: mail.arrival_status
                  });
                }
              });
            }
            return;
          }
        } catch (fallbackErr) {
          console.error("Fallback IMAP fetch also failed:", fallbackErr);
        }
      }
      // Silently handle error - UI will show empty state
      setMails([]);
    } finally {
      setLoading(false);
    }
  };

  // Try to fetch full body for a single mail if FullBody or sections missing.
  const fetchFullMail = async (index) => {
    setFetchingFull(true);
    try {
      const resp = await axios.post("https://shipreportstvm.cloud/api/get_mail_full", { index });
      if (resp && resp.status === 200 && resp.data) {
        const updated = [...mails];
        const mailFull = resp.data;
        updated[index] = {
          ...updated[index],
          FullBody: mailFull.FullBody || updated[index].FullBody,
          Headers: mailFull.Headers || updated[index].Headers,
          coords_extracted: mailFull.coords_extracted || updated[index].coords_extracted,
          coords: mailFull.coords || updated[index].coords,
          __raw: { ...updated[index].__raw, ...mailFull }, // merge sections if present
        };
        setMails(updated);
        return updated[index];
      } else {
        console.warn("No full-body returned from server for index", index);
        return null;
      }
    } catch (err) {
      console.error("Failed to fetch full mail:", err);
      return null;
    } finally {
      setFetchingFull(false);
    }
  };

  // Initialize selection state from mail.__raw.sections when modal opens
  const initSelectionFromMail = async (mailObj, indexInState) => {
    const src = mailObj.__raw;
    let sections = src.sections || src.sections_filtered || null;

    // Check if we already have saved selections loaded from earlier
    const existingSelections = mailSelections[indexInState];

    // If no sections, try to fetch full mail
    if (!sections) {
      const updatedMail = await fetchFullMail(indexInState);
      const newSrc = updatedMail ? updatedMail.__raw : mailObj.__raw;
      const sec = newSrc.sections || newSrc.sections_filtered || {};
      
      // Try to load saved view for this specific mail first
      let loaded = existingSelections;
      if (!loaded || Object.keys(loaded).length === 0) {
        loaded = await serverLoadSelection(updatedMail ?? mailObj);
      }
      
      // If no saved view for this mail, try to find similar view from same ship
      if (!loaded || Object.keys(loaded).length === 0) {
        loaded = await serverLoadSimilarView(updatedMail ?? mailObj);
      }
      
      if (loaded && typeof loaded === "object") {
        const merged = mergeSavedIntoDefaults(sec, loaded);
        setSectionSelection(merged);
        // Update mail selections for preview
        setMailSelections((prevSelections) => ({
          ...prevSelections,
          [indexInState]: { ...merged }
        }));
      } else {
        // No saved view found - default all deselected
        buildSelectionFromSections(sec);
        // Update mail selections for preview
        const sel = {};
        Object.keys(sec || {}).forEach((secNo) => {
          const section = sec[secNo];
          const lines = section.lines || [];
          const lineSel = {};
          // Default: all lines deselected (false)
          lines.forEach((_, idx) => (lineSel[idx] = false));
          sel[secNo] = { enabled: false, lines: lineSel, title: section.title || "" };
        });
        setMailSelections((prevSelections) => ({
          ...prevSelections,
          [indexInState]: sel
        }));
      }
      setSelectedMail({ ...updatedMail ?? mailObj, index: indexInState });
      return;
    }

    // sections exist: use existing selections if available, otherwise load from server
    let loaded = existingSelections;
    if (!loaded || Object.keys(loaded).length === 0) {
      loaded = await serverLoadSelection(mailObj);
    }
    
    // If no saved view for this mail, try to find similar view from same ship
    if (!loaded || Object.keys(loaded).length === 0) {
      loaded = await serverLoadSimilarView(mailObj);
    }
    
    if (loaded && typeof loaded === "object") {
      const merged = mergeSavedIntoDefaults(sections, loaded);
      setSectionSelection(merged);
      // Update mail selections for preview
      setMailSelections((prevSelections) => ({
        ...prevSelections,
        [indexInState]: { ...merged }
      }));
    } else {
      // No saved view found - default all deselected
      buildSelectionFromSections(sections);
      // Update mail selections for preview
      const sel = {};
      Object.keys(sections || {}).forEach((secNo) => {
        const sec = sections[secNo];
        const lines = sec.lines || [];
        const lineSel = {};
        // Default: all lines deselected (false)
        lines.forEach((_, idx) => (lineSel[idx] = false));
        sel[secNo] = { enabled: false, lines: lineSel, title: sec.title || "" };
      });
      setMailSelections((prevSelections) => ({
        ...prevSelections,
        [indexInState]: sel
      }));
    }
    setSelectedMail({ ...mailObj, index: indexInState });
  };

  // Merge server-loaded saved selection into defaults built from sections
  // By default, all lines are deselected (false)
  const mergeSavedIntoDefaults = (sections, loaded) => {
    const selDefaults = {};
    Object.keys(sections || {}).forEach((secNo) => {
      const sec = sections[secNo];
      const lines = sec.lines || [];
      const lineSel = {};
      // Default: all lines deselected (false)
      lines.forEach((_, idx) => (lineSel[idx] = false));
      selDefaults[secNo] = { enabled: false, lines: lineSel, title: sec.title || "" };
    });

    // merge loaded selections (only apply enabled sections and their selected lines)
    Object.keys(loaded || {}).forEach((sno) => {
      if (selDefaults[sno]) {
        if (typeof loaded[sno].enabled === "boolean") selDefaults[sno].enabled = loaded[sno].enabled;
        if (loaded[sno].lines && typeof loaded[sno].lines === "object") {
          // Only apply selected lines (true values) from saved view
          Object.keys(loaded[sno].lines).forEach((lineIdx) => {
            if (loaded[sno].lines[lineIdx] === true) {
              selDefaults[sno].lines[lineIdx] = true;
            }
          });
        }
      } else {
        // keep saved entries even if section absent now
        selDefaults[sno] = loaded[sno];
      }
    });
    return selDefaults;
  };

  // Build selection object from sections (no server merge)
  // By default, all lines are deselected (false)
  const buildSelectionFromSections = (sections) => {
    const sel = {};
    Object.keys(sections || {}).forEach((secNo) => {
      const sec = sections[secNo];
      const lines = sec.lines || [];
      const lineSel = {};
      // Default: all lines deselected (false)
      lines.forEach((_, idx) => (lineSel[idx] = false));
      sel[secNo] = { enabled: false, lines: lineSel, title: sec.title || "" };
    });
    setSectionSelection(sel);
  };

  const handleViewMail = async (index) => {
    const m = mails[index];
    if (!m) return;

    // if FullBody or sections exist already, open modal and initialize selection
    if ((m.__raw && (m.__raw.sections || m.__raw.sections_filtered)) || m.FullBody) {
      await initSelectionFromMail(m, index);
      return;
    }

    // otherwise try on-demand fetch and then open
    const fetched = await fetchFullMail(index);
    if (fetched) {
      await initSelectionFromMail(fetched, index);
    } else {
      // fallback - show what we have
      const s = m.__raw?.sections || m.__raw?.sections_filtered || {};
      buildSelectionFromSections(s);
      setSelectedMail({ ...m, index });
    }
  };

  const closeModal = () => setSelectedMail(null);

  // Toggle a whole section enabled/disabled (and persist)
  const toggleSection = (secNo) => {
    setSectionSelection((prev) => {
      const copy = { ...prev };
      copy[secNo] = { ...copy[secNo], enabled: !copy[secNo].enabled };
      // optimistic save to server (fire-and-forget)
      setTimeout(() => {
        serverSaveSelection(selectedMail, copy).then((ok) => {
          if (!ok) console.warn("Failed to save selection to server (toggleSection)");
        });
      }, 0);
      // Update mail selections for preview
      if (selectedMail && selectedMail.index !== undefined) {
        setMailSelections((prevSelections) => ({
          ...prevSelections,
          [selectedMail.index]: { ...copy }
        }));
      }
      return copy;
    });
  };

  // Toggle a single line inside a section (and persist)
  const toggleLine = (secNo, lineIdx) => {
    setSectionSelection((prev) => {
      const copy = { ...prev };
      const sec = copy[secNo] ? { ...copy[secNo] } : { enabled: true, lines: {} };
      sec.lines = { ...sec.lines, [lineIdx]: !sec.lines[lineIdx] };
      copy[secNo] = sec;

      setTimeout(() => {
        serverSaveSelection(selectedMail, copy).then((ok) => {
          if (!ok) console.warn("Failed to save selection to server (toggleLine)");
        });
      }, 0);

      // Update mail selections for preview
      if (selectedMail && selectedMail.index !== undefined) {
        setMailSelections((prevSelections) => ({
          ...prevSelections,
          [selectedMail.index]: { ...copy }
        }));
      }

      return copy;
    });
  };

  // Clear saved view on server and reset selection to defaults for this mail
  const handleClearSavedView = async () => {
    if (!selectedMail) return;
    const ok = await serverDeleteView(selectedMail);
    if (ok) {
      const sections = selectedMail.__raw?.sections || selectedMail.__raw?.sections_filtered || {};
      buildSelectionFromSections(sections);
      // Update mail selections for preview - default all deselected
      const sel = {};
      Object.keys(sections || {}).forEach((secNo) => {
        const sec = sections[secNo];
        const lines = sec.lines || [];
        const lineSel = {};
        // Default: all lines deselected (false)
        lines.forEach((_, idx) => (lineSel[idx] = false));
        sel[secNo] = { enabled: false, lines: lineSel, title: sec.title || "" };
      });
      setMailSelections((prevSelections) => ({
        ...prevSelections,
        [selectedMail.index]: sel
      }));
      alert("Saved view cleared for this mail.");
    } else {
      // If no saved view exists, just reset to defaults without error
      const sections = selectedMail.__raw?.sections || selectedMail.__raw?.sections_filtered || {};
      if (Object.keys(sections).length > 0) {
        buildSelectionFromSections(sections);
        const sel = {};
        Object.keys(sections || {}).forEach((secNo) => {
          const sec = sections[secNo];
          const lines = sec.lines || [];
          const lineSel = {};
          // Default: all lines deselected (false)
          lines.forEach((_, idx) => (lineSel[idx] = false));
          sel[secNo] = { enabled: false, lines: lineSel, title: sec.title || "" };
        });
        setMailSelections((prevSelections) => ({
          ...prevSelections,
          [selectedMail.index]: sel
        }));
      }
      // Don't show error if no saved view exists - just reset silently
    }
  };

  // Expand/collapse a section in the left panel
  const toggleExpand = (secNo) => {
    setExpandedSections((prev) => ({ ...prev, [secNo]: !prev[secNo] }));
  };

  // Toggle select all / deselect all sections
  const toggleSelectAll = () => {
    if (!selectedMail) return;
    const src = selectedMail.__raw || {};
    const sections = src.sections || src.sections_filtered || {};
    const currentSelection = sectionSelection;
    
    // Check if all sections are currently enabled
    const allEnabled = Object.keys(sections).every((secNo) => {
      const sel = currentSelection[secNo];
      return sel && sel.enabled;
    });
    
    // Create new selection state
    const newSelection = {};
    Object.keys(sections).forEach((secNo) => {
      const sec = sections[secNo];
      const lines = sec.lines || [];
      const lineSel = {};
      
      // If deselecting all, set enabled to false and all lines to false
      // If selecting all, set enabled to true and all lines to true
      lines.forEach((_, idx) => {
        lineSel[idx] = !allEnabled;
      });
      
      newSelection[secNo] = {
        enabled: !allEnabled,
        lines: lineSel,
        title: sec.title || ""
      };
    });
    
    setSectionSelection(newSelection);
    
    // Save to server
    setTimeout(() => {
      serverSaveSelection(selectedMail, newSelection).then((ok) => {
        if (!ok) console.warn("Failed to save selection to server (toggleSelectAll)");
      });
    }, 0);
    
    // Update mail selections for preview
    if (selectedMail && selectedMail.index !== undefined) {
      setMailSelections((prevSelections) => ({
        ...prevSelections,
        [selectedMail.index]: { ...newSelection }
      }));
    }
  };

  // Build preview text from selections for display in modal's preview pane
  // Includes section title, selected lines, KV pairs, and metrics
  const buildPreviewText = (mailObj = null, selections = null) => {
    const mail = mailObj || selectedMail;
    const sel = selections || sectionSelection;
    if (!mail) return "(no mail selected)";
    const src = mail.__raw || {};
    const sections = src.sections || src.sections_filtered || {};
    const parts = [];

    // Sort sections by number for consistent ordering
    const sortedSecNos = Object.keys(sel).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });

    sortedSecNos.forEach((secNo) => {
      const sectionSel = sel[secNo];
      if (!sectionSel || !sectionSel.enabled) return;
      const sec = sections[secNo];
      if (!sec) return;
      
      const sectionParts = [];
      
      // Add section title (e.g., "2. Position")
      const sectionTitle = `${secNo}. ${sec.title || "(no title)"}`;
      sectionParts.push(sectionTitle);
      
      // Add selected lines
      const lines = sec.lines || [];
      const selectedLines = Object.keys(sectionSel.lines || {})
        .map((k) => parseInt(k, 10))
        .filter((li) => sectionSel.lines[li])
        .sort((a, b) => a - b)
        .map((li) => {
          const text = (lines[li] || "").trim();
          return text;
        })
        .filter((text) => text.length > 0);
      
      if (selectedLines.length > 0) {
        sectionParts.push(...selectedLines);
      }
      
      // Add KV pairs if section is enabled
      if (sec.kv && Object.keys(sec.kv).length > 0) {
        Object.entries(sec.kv).forEach(([k, v]) => {
          if (v && v.trim().length > 0) {
            sectionParts.push(`${k}: ${v}`);
          }
        });
      }
      
      // Add metrics if section is enabled
      if (sec.metrics && Object.keys(sec.metrics).length > 0) {
        Object.entries(sec.metrics).forEach(([mk, mv]) => {
          if (mv !== null && mv !== undefined) {
            if (typeof mv === "object") {
              Object.entries(mv).forEach(([kk, vv]) => {
                sectionParts.push(`${mk}.${kk}: ${String(vv)}`);
              });
            } else {
              sectionParts.push(`${mk}: ${String(mv)}`);
            }
          }
        });
      }
      
      // Add section if it has content beyond just the title
      // Show section if it has: selected lines, KV pairs, or metrics
      const hasContent = selectedLines.length > 0 || 
                        (sec.kv && Object.keys(sec.kv).length > 0) || 
                        (sec.metrics && Object.keys(sec.metrics).length > 0);
      
      if (hasContent && sectionParts.length > 0) {
        parts.push(sectionParts.join("\n"));
      }
    });

    if (parts.length === 0) {
      return mail.FullBody ?? mail.body ?? "(no selected content)";
    }
    return parts.join("\n\n");
  };

  // Get selected data preview for a mail (for display below subject)
  // Returns selected content if available, otherwise returns email body by default
  const getSelectedDataPreview = (mailIndex) => {
    const mail = mails[mailIndex];
    if (!mail) return null;
    const selections = mailSelections[mailIndex];
    
    // If selections exist and have content, return selected preview
    if (selections && Object.keys(selections).length > 0) {
      const selectedText = buildPreviewText(mail, selections);
      if (selectedText && selectedText.trim().length > 0) {
        return selectedText;
      }
    }
    
    // Default: return email body (FullBody if available, otherwise body)
    return mail.FullBody || mail.body || null;
  };

  // Get first 25-35 words from text
  const getWordsPreview = (text, minWords = 25, maxWords = 30) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(" ");
  };

  // Render parsed metrics from mail (if any)
  const renderMetrics = (mail) => {
    const src = mail.__raw || {};
    const sections = src.sections || src.sections_filtered || {};
    const metricsAggregate = {};
    Object.values(sections).forEach((sec) => {
      if (sec.metrics) {
        Object.keys(sec.metrics).forEach((k) => {
          metricsAggregate[k] = sec.metrics[k];
        });
      }
    });
    if (!Object.keys(metricsAggregate).length) return null;

    return (
      <div style={{ marginTop: 10 }}>
        <strong>Parsed metrics</strong>
        <div className="small" style={{ marginTop: 6 }}>
          {Object.entries(metricsAggregate).map(([k, v]) => {
            if (v && typeof v === "object") {
              return (
                <div key={k} style={{ marginBottom: 6 }}>
                  <div style={{ textTransform: "capitalize" }}>{k}:</div>
                  <div style={{ marginLeft: 8 }}>
                    {Object.entries(v).map(([kk, vv]) => (
                      <div key={kk}>
                        <code>{kk}</code>: {String(vv)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <div key={k}>
                <code>{k}</code>: {String(v)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------- JSX (UI) ----------
  return (
    <div className="col-lg-4" style={{ borderLeft: "1px solid #e5e7eb" }}>
      <SimpleBackdrop showBackDrop={loading} />
      <div className="card">
        {/* <div className="card-header d-flex align-items-center justify-content-between">
          <strong>All Mail</strong>
          <span className="badge badge-primary">{mails.length}</span>
        </div> */}

        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : mails.length === 0 ? (
            <div className="text-muted small">No mails loaded yet.</div>
          ) : (
            <ul className="list-group list-group-flush" style={{ marginBottom: 0}}>
              {mails.slice(0, 5).map((m, index) => {
                const previewContent = getSelectedDataPreview(index);
                const hasContent = previewContent && previewContent.trim().length > 0;
                const wordsPreview = hasContent ? getWordsPreview(previewContent) : "";
                const totalWords = hasContent ? previewContent.trim().split(/\s+/).length : 0;
                return (
                <li key={m.id ?? index} className="list-group-item" style={{ padding: "10px"}}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
  <div className="font-weight-bold text-truncate" title={m.subject} style={{ fontSize: "15px" }}>
    {m.subject}
  </div>
  {/* Received date under subject */}
  <div className="text-muted small" style={{ marginTop: 1, fontSize: "10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={m.received || m.Headers?.Date}>
    Received: {formatDate(m.received || m.Headers?.Date)}
  </div>
</div>


                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* Plot and Remove Marker buttons commented out */}
                        {/* {m.coords_extracted ? (
                          m.plotButtonVisible ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              title="Plot"
                              onClick={() =>
                                plotOrRemoveButtonClicked({
                                  subject: m.subject,
                                  mailContent: m.body,
                                  lat: m.lat,
                                  long: m.long,
                                  ship_name: m.ship_name,
                                  clickedButton: "plot",
                                  itemIndex: index,
                                })
                              }
                            >
                            <i className="fas fa-location"></i>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Remove Marker"
                              onClick={() =>
                                plotOrRemoveButtonClicked({
                                  subject: m.subject,
                                  mailContent: m.body,
                                  lat: m.lat,
                                  long: m.long,
                                  ship_name: m.ship_name,
                                  clickedButton: "remove",
                                  itemIndex: index,
                                })
                              }
                            >
                              <i className="fas fa-minus-circle"></i>
                            </button>
                          )
                        ) : (
                          <button type="button" className="btn btn-sm btn-outline-secondary" disabled title="No coords">
                            N/A
                          </button>
                        )} */}

                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          title="View mail"
                          onClick={() => handleViewMail(index)}
                        >
                          <i className="fas fa-eye" />
                        </button>

                        {m.attachments && m.attachments.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-outline-info d-flex align-items-center justify-content-center"
                            title="View attachments"
                            onClick={() => setViewingAttachments(index)}
                            style={{
                              width: "60px",
                              height: "28px",
                              padding: ".25rem .5rem",
                              fontSize: ".875rem",
                              lineHeight: "1.5",
                              borderRadius: ".2rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                            }}
                          >
                            <i className="fas fa-paperclip"></i>
                            <span
                              className="badge bg-light text-dark"
                              style={{
                                fontSize: "9px",
                                minWidth: "16px",
                                height: "14px",
                                lineHeight: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "8px",
                              }}
                            >
                              {m.attachments.length}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Fixed size preview box below email header - shows 25-35 words with Read More on new line */}
                    <div style={{ 
                      // marginTop: "6px", 
                      padding: "4px 6px", 
                      background: "#f8f9fa", 
                      borderRadius: "4px",
                      border: "1px solid #dee2e6",
                      minHeight: "60px", // Flexible height for 25-35 words
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      {hasContent ? (
                        <>
                          <div style={{ 
                            fontSize: "12px",
                            lineHeight: "1.3",
                            overflow: "hidden",
                            wordBreak: "break-word",
                            display: "-webkit-box",     // clamp to max 2 lines visually
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            flex: 1,
                          }}>
                            {wordsPreview}
                            {totalWords > 25 && <span>...</span>}
                          </div>
                          {totalWords > 25 && (               <button
                              type="button"
                              className="btn btn-sm btn-link"
                              style={{ 
                                padding: "2px 0", 
                                fontSize: "11px", 
                                textDecoration: "none", 
                                color: "#007bff", 
                                alignSelf: "flex-start",
                                marginTop: "4px"
                              }}
                             // Replace the existing onClick for the Read More button with this:
onClick={async () => {
  try {
    setFetchingFull(true);
    // try to fetch and update the mail in state (uses your existing helper)
    const fetched = await fetchFullMail(index);
    // prefer FullBody, then body, then the previous selected preview
    const fullText =
      (fetched && (fetched.FullBody || fetched.body)) ||
      (m.FullBody || m.body) ||
      buildPreviewText(m, mailSelections[index] || {});
    setReadMoreModal({
      mailIndex: index,
      selectedData: fullText,
      subject: m.subject,
    });
  } catch (e) {
    // fallback to whatever we have
    const fullText = (m.FullBody || m.body) || buildPreviewText(m, mailSelections[index] || {});
    setReadMoreModal({
      mailIndex: index,
      selectedData: fullText,
      subject: m.subject,
    });
  } finally {
    setFetchingFull(false);
  }
}}
                            >
                              Read More →
                            </button>)}
                        </>
                      ) : (
                        <div style={{ 
                          fontSize: "11px", 
                          color: "#999", 
                          fontStyle: "italic",
                          display: "flex",
                          alignItems: "center",
                          height: "100%"
                        }}>
                          No email content available.
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )})}
            </ul>
          )}
        </div>
      </div>

      {/* Attachment Viewer Modal */}
      {viewingAttachments !== null && mails[viewingAttachments] && (
        <div
          className="modal-overlay"
          onClick={() => setViewingAttachments(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            className="modal-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: 800,
              maxHeight: "90%",
              overflowY: "auto",
              background: "white",
              borderRadius: 8,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ margin: 0 }}>
                Attachments - {mails[viewingAttachments].subject || "No subject"}
              </h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setViewingAttachments(null)}
              >
                Close
              </button>
            </div>
            <AttachmentViewer
              attachments={mails[viewingAttachments].attachments || []}
              onAttachmentClick={(attachment) => setFullScreenAttachment(attachment)}
            />
          </div>
        </div>
      )}

      {/* Full Screen Attachment Viewer */}
      {fullScreenAttachment && (
        <FullScreenViewer
          attachment={fullScreenAttachment}
          onClose={() => setFullScreenAttachment(null)}
        />
      )}

      {/* Modal overlay for viewing mail + section-selection UI */}
      {selectedMail && (
        <div
          className="modal-overlay"
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            className="modal-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "96%",
              maxWidth: 1200,
              maxHeight: "94%",
              overflowY: "auto",
              background: "white",
              borderRadius: 8,
              padding: 18,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              display: "flex",
              gap: 16,
            }}
          >
            <div style={{ flex: "0 0 420px", overflowY: "auto", paddingRight: 8 }}>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h5 style={{ margin: 0 }}>{selectedMail.Headers?.Subject || selectedMail.subject}</h5>
                  <div className="text-muted small" style={{ marginTop: 6 }}>
                    From: {selectedMail.Headers?.From || selectedMail.from} &nbsp; • &nbsp;
                    To: {selectedMail.Headers?.To || selectedMail.to || ""} &nbsp; • &nbsp;
                    Received: {selectedMail.Headers?.Date || selectedMail.received}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" className="btn btn-sm btn-outline-danger" title="Close" onClick={closeModal}>
                  <i className = "fa-solid fa-xmark"></i>
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" title="Clear saved view" onClick={handleClearSavedView}>
                  <i className ="fa-solid fa-pen"></i>
                  </button>
                </div>
              </div>

              <hr />

              <div>
                <strong>Parsed sections</strong>
                <div className="small text-muted" style={{ marginBottom: 8 }}>
                  Toggle sections and individual lines you want displayed in preview.
                </div>

                {/* Select All / Deselect All checkbox */}
                {(() => {
                  const src = selectedMail.__raw || {};
                  const sections = src.sections || src.sections_filtered || {};
                  if (!sections || Object.keys(sections).length === 0) {
                    return null;
                  }
                  
                  // Check if all sections are enabled and all lines within each section are selected
                  const sectionKeys = Object.keys(sections);
                  const allEnabled = sectionKeys.length > 0 && sectionKeys.every((secNo) => {
                    const sel = sectionSelection[secNo];
                    if (!sel || !sel.enabled) return false;
                    // Check if all lines in the section are selected
                    const sec = sections[secNo];
                    const lines = sec.lines || [];
                    if (lines.length === 0) return true; // Section with no lines is considered enabled
                    return lines.every((_, idx) => sel.lines && sel.lines[idx] === true);
                  });
                  
                  return (
                    <div style={{ marginBottom: 12, padding: "8px", background: "#f8f9fa", borderRadius: "4px", border: "1px solid #dee2e6" }}>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: 600 }}>
                        <input
                          type="checkbox"
                          checked={allEnabled}
                          onChange={toggleSelectAll}
                          style={{ marginRight: 8, cursor: "pointer" }}
                        />
                        {allEnabled ? "Deselect All" : "Select All"}
                      </label>
                    </div>
                  );
                })()}

                {/* left panel: list of sections */}
                {(() => {
                  const src = selectedMail.__raw || {};
                  const sections = src.sections || src.sections_filtered || {};
                  if (!sections || Object.keys(sections).length === 0) {
                    return <div className="text-muted small">No parsed sections found for this mail.</div>;
                  }

                  return Object.keys(sections).map((secNo) => {
                    const sec = sections[secNo];
                    const sel = sectionSelection[secNo] || { enabled: true, lines: {} };
                    const lines = sec.lines || [];

                    return (
                      <div key={secNo} style={{ borderBottom: "1px solid #eee", padding: "8px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div>
                            <input
                              id={`chk-sec-${secNo}`}
                              type="checkbox"
                              checked={!!sel.enabled}
                              onChange={() => toggleSection(secNo)}
                            />{" "}
                            <label htmlFor={`chk-sec-${secNo}`} style={{ fontWeight: 600 }}>
                              {secNo}. {sec.title || "(no title)"}
                            </label>
                          </div>
                          <div>
                            <button
                              className="btn btn-sm btn-link"
                              onClick={() => toggleExpand(secNo)}
                              style={{ textDecoration: "none" }}
                            >
                              {expandedSections[secNo] ? "Collapse" : "Expand"}
                            </button>
                          </div>
                        </div>

                        {expandedSections[secNo] && (
                          <div style={{ marginTop: 8, paddingLeft: 8 }}>
                            <div className="small text-muted">Pick lines to show:</div>
                            {lines.length === 0 ? (
                              <div className="text-muted small">No lines parsed.</div>
                            ) : (
                              lines.map((ln, li) => (
                                <div key={li} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!(sel.lines && sel.lines[li])}
                                    onChange={() => toggleLine(secNo, li)}
                                  />
                                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{ln}</div>
                                </div>
                              ))
                            )}

                            {/* show kv pairs if present */}
                            {sec.kv && Object.keys(sec.kv).length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div className="small text-muted">Key → Value</div>
                                <div style={{ marginLeft: 8 }}>
                                  {Object.entries(sec.kv).map(([k, v]) => (
                                    <div key={k} style={{ fontSize: 13 }}>
                                      <strong>{k}</strong>: {v}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* show metrics */}
                            {sec.metrics && Object.keys(sec.metrics).length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div className="small text-muted">Metrics</div>
                                <div style={{ marginLeft: 8 }}>
                                  {Object.entries(sec.metrics).map(([mk, mv]) => (
                                    <div key={mk} style={{ fontSize: 13 }}>
                                      <code>{mk}</code>:{" "}
                                      {mv && typeof mv === "object"
                                        ? Object.entries(mv).map(([kk, vv]) => (
                                            <div key={kk}><small>{kk}: {String(vv)}</small></div>
                                          ))
                                        : String(mv)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* right panel: preview and metrics */}
            <div style={{ flex: 1, overflowY: "auto", paddingLeft: 8 }}>
              <div>
                <strong>Preview</strong>
                <div className="small text-muted" style={{ marginBottom: 8 }}>
                  This is what will be shown for the selected sections/lines.
                </div>

                <pre style={{ whiteSpace: "pre-wrap", background: "#f8f9fa", padding: 12, borderRadius: 6 }}>
{buildPreviewText()}
                </pre>
              </div>

              {/* show parsed metrics aggregate */}
              {renderMetrics(selectedMail)}

              <div style={{ marginTop: 12 }}>
                <strong>Detected coords:</strong> {selectedMail.coords_extracted || selectedMail.coords || "None"}
              </div>

              <div style={{ marginTop: 8 }}>
                <strong>Raw headers / debug:</strong>
                <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", marginTop: 6 }}>
{JSON.stringify(selectedMail.Headers || { From: selectedMail.from, To: selectedMail.to, Subject: selectedMail.subject, Date: selectedMail.received }, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* optional spinner when fetching full body */}
      {fetchingFull && (
        <div style={{ position: "fixed", right: 12, bottom: 12, zIndex: 1100 }}>
          <div className="badge badge-info p-2">Fetching full mail…</div>
        </div>
      )}

      {/* Read More Modal - shows only selected data with three buttons */}
      {readMoreModal && (
        <div
          className="modal-overlay"
          onClick={() => setReadMoreModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            className="modal-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: 800,
              maxHeight: "90%",
              overflowY: "auto",
              background: "white",
              borderRadius: 8,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ margin: 0 }}>
                {readMoreModal.subject || "Selected Data Preview"}
              </h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setReadMoreModal(null)}
              >
                Close
              </button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div className="text-muted small">
                Ship: {mails[readMoreModal.mailIndex]?.ship_name || "Unknown Ship"}
              </div>
            </div>
            <hr />
            <div>
              <strong>Selected Data:</strong>
              <pre style={{ 
                whiteSpace: "pre-wrap", 
                background: "#f8f9fa", 
                padding: 12, 
                borderRadius: 6,
                marginTop: 8,
                fontSize: "13px",
                lineHeight: "1.5"
              }}>
                {readMoreModal.selectedData || "(no selected content)"}
              </pre>
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {/* Plot and Remove Marker buttons commented out */}
              {/* {mails[readMoreModal.mailIndex]?.coords_extracted ? (
                mails[readMoreModal.mailIndex]?.plotButtonVisible ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    title="Plot"
                    onClick={() => {
                      const m = mails[readMoreModal.mailIndex];
                      plotOrRemoveButtonClicked({
                        subject: m.subject,
                        mailContent: m.body,
                        lat: m.lat,
                        long: m.long,
                        ship_name: m.ship_name,
                        clickedButton: "plot",
                        itemIndex: readMoreModal.mailIndex,
                      });
                    }}
                  >
                    <i className="fas fa-location"></i> Plot
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Remove Marker"
                    onClick={() => {
                      const m = mails[readMoreModal.mailIndex];
                      plotOrRemoveButtonClicked({
                        subject: m.subject,
                        mailContent: m.body,
                        lat: m.lat,
                        long: m.long,
                        ship_name: m.ship_name,
                        clickedButton: "remove",
                        itemIndex: readMoreModal.mailIndex,
                      });
                    }}
                  >
                    <i className="fas fa-minus-circle"></i> 
                  </button>
                )
              ) : null} */}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title="View mail"
                onClick={() => {
                  setReadMoreModal(null);
                  handleViewMail(readMoreModal.mailIndex);
                }}
              >
                <i className="fas fa-eye" /> 
              </button>
              {mails[readMoreModal.mailIndex]?.attachments && mails[readMoreModal.mailIndex].attachments.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm"
                  title="View attachments"
                  onClick={() => {
                    setReadMoreModal(null);
                    setViewingAttachments(readMoreModal.mailIndex);
                  }}
                >
                  <i className="fas fa-paperclip" /> ({mails[readMoreModal.mailIndex].attachments.length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
